import { z } from "zod";

import { type Locale } from "./locale.js";
import { LocalizedText, pickLocalizedText } from "./localizedText.js";
import {
  PlannedExerciseEntry,
  type PlannedExerciseEntry as PlannedExerciseEntryT,
} from "./schemas/plan.js";
import {
  ProgramSession,
  Slot,
  type ProgramSession as ProgramSessionT,
  type Slot as SlotT,
} from "./schemas/program.js";

// Template SCHEMAS + resolvers. Split out of templates.ts (SP-TEMPLATES,
// 2026-07-27) to get that file under the 500-line cap; the hand-authored
// template DATA lives in templateData.ts, and templates.ts re-exports both.
//
// ⚠️ Dependency direction is one-way and must stay that way: templateData
// imports from here, never the reverse. These schemas are evaluated at
// module load (zod builders run top-level), so a cycle would not be a type
// error — it would be a TDZ crash the first time anything imports templates.
// ─── Template-scoped types (LocalizedText fields override runtime
// schemas; non-text fields are pulled through verbatim) ───────────

const PlanTemplateEntry = PlannedExerciseEntry.omit({ notes: true }).extend({
  notes: LocalizedText.optional(),
});
export type PlanTemplateEntry = z.infer<typeof PlanTemplateEntry>;

export const PlanTemplate = z.object({
  id: z.string().min(1).max(64),
  name: LocalizedText,
  // Short markdown-light description for the templates picker. Plain
  // text only; no HTML, no link rendering.
  description: LocalizedText,
  // Difficulty hint shown as a chip. Three rungs is enough for a
  // starter set; we can grow the enum later without breaking clients.
  difficulty: z.enum(["beginner", "intermediate", "advanced"]),
  // Coarse tag set for filtering ("push", "pull", "legs", "full-body",
  // "strength", "hypertrophy"). Free-form lowercase slugs — not
  // localized (the picker labels them via the i18n catalog if it ever
  // grows a tag filter UI).
  tags: z.array(z.string().min(1).max(32)).max(8),
  notes: LocalizedText.optional(),
  // Same wire shape as Plan.entries except entry.notes is LocalizedText.
  entries: z.array(PlanTemplateEntry).min(1).max(40),
});
export type PlanTemplate = z.infer<typeof PlanTemplate>;

const ProgramTemplateSlot = Slot.omit({ instructions: true }).extend({
  instructions: LocalizedText.optional(),
});
export type ProgramTemplateSlot = z.infer<typeof ProgramTemplateSlot>;

const ProgramTemplateSession = ProgramSession.omit({
  name: true,
  notes: true,
  slots: true,
}).extend({
  name: LocalizedText,
  notes: LocalizedText.optional(),
  slots: z.array(ProgramTemplateSlot).min(1).max(20),
});
export type ProgramTemplateSession = z.infer<typeof ProgramTemplateSession>;

export const ProgramTemplate = z.object({
  id: z.string().min(1).max(64),
  name: LocalizedText,
  description: LocalizedText,
  difficulty: z.enum(["beginner", "intermediate", "advanced"]),
  tags: z.array(z.string().min(1).max(32)).max(8),
  cycleWeeks: z.number().int().min(1).max(12),
  sessionsPerWeek: z.number().int().min(1).max(7),
  sessions: z.array(ProgramTemplateSession).min(1).max(7),
});
export type ProgramTemplate = z.infer<typeof ProgramTemplate>;

// ─── Resolvers — collapse LocalizedText fields into the user's locale
// so the result drops cleanly into the runtime Plan / Program clone
// path and into the templates picker render. EN fallback is built in
// via pickLocalizedText.

export type ResolvedPlanTemplate = {
  id: string;
  name: string;
  description: string;
  difficulty: PlanTemplate["difficulty"];
  tags: string[];
  notes?: string;
  entries: PlannedExerciseEntryT[];
};

export const resolvePlanTemplate = (
  tpl: PlanTemplate,
  lang: Locale,
): ResolvedPlanTemplate => ({
  id: tpl.id,
  name: pickLocalizedText(tpl.name, lang),
  description: pickLocalizedText(tpl.description, lang),
  difficulty: tpl.difficulty,
  tags: tpl.tags,
  ...(tpl.notes ? { notes: pickLocalizedText(tpl.notes, lang) } : {}),
  entries: tpl.entries.map((e) => ({
    exerciseId: e.exerciseId,
    ...(e.exerciseName !== undefined
      ? { exerciseName: e.exerciseName }
      : {}),
    sets: e.sets,
    ...(e.notes ? { notes: pickLocalizedText(e.notes, lang) } : {}),
    ...(e.group ? { group: e.group } : {}),
  })),
});

export type ResolvedProgramTemplate = {
  id: string;
  name: string;
  description: string;
  difficulty: ProgramTemplate["difficulty"];
  tags: string[];
  cycleWeeks: number;
  sessionsPerWeek: number;
  sessions: ProgramSessionT[];
};

const resolveSlot = (s: ProgramTemplateSlot, lang: Locale): SlotT => ({
  id: s.id,
  position: s.position,
  variants: s.variants,
  weeks: s.weeks,
  ...(s.amrapLastSet ? { amrapLastSet: true } : {}),
  ...(s.maxAttempt ? { maxAttempt: true } : {}),
  ...(s.instructions
    ? { instructions: pickLocalizedText(s.instructions, lang) }
    : {}),
  ...(s.group ? { group: s.group } : {}),
});

export const resolveProgramTemplate = (
  tpl: ProgramTemplate,
  lang: Locale,
): ResolvedProgramTemplate => ({
  id: tpl.id,
  name: pickLocalizedText(tpl.name, lang),
  description: pickLocalizedText(tpl.description, lang),
  difficulty: tpl.difficulty,
  tags: tpl.tags,
  cycleWeeks: tpl.cycleWeeks,
  sessionsPerWeek: tpl.sessionsPerWeek,
  sessions: tpl.sessions.map((sess) => ({
    id: sess.id,
    name: pickLocalizedText(sess.name, lang),
    slots: sess.slots.map((slot) => resolveSlot(slot, lang)),
    ...(sess.notes ? { notes: pickLocalizedText(sess.notes, lang) } : {}),
  })),
});
